import React from 'react';
import { Button } from './ui/button';
import { 
    Share2, 
    Twitter,
    Copy,
    CheckCircle,
    ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { useState } from 'react';

import { getPublicOrigin } from '../lib/publicUrl';

const APP_URL = getPublicOrigin();

// Generate shareable text for Twitter/X
const generateShareText = (bet, winAmount, userRole) => {
    const platform = 'Bet4Peer';
    const hashtags = '#Web3 #Crypto #Betting #Bet4Peer';
    
    const text = `Just won ${winAmount.toFixed(4)} ETH on ${platform}.

"${bet.description?.slice(0, 60)}${bet.description?.length > 60 ? '...' : ''}"

${hashtags}`;
    
    return text;
};

// Twitter/X Share Button
export function TwitterShareButton({ bet, winAmount, userRole, className = '' }) {
    const shareText = generateShareText(bet, winAmount, userRole);
    const betUrl = `${APP_URL}/bets/${bet.id}`;
    
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(betUrl)}`;
    
    const handleShare = () => {
        window.open(twitterUrl, '_blank', 'width=600,height=400');
    };
    
    return (
        <Button
            onClick={handleShare}
            className={`bg-[#1DA1F2] hover:bg-[#1a8cd8] text-white ${className}`}
            size="sm"
        >
            <Twitter className="w-4 h-4 mr-2" />
            Share on X
        </Button>
    );
}

// Copy Link Button
export function CopyLinkButton({ betId, className = '' }) {
    const [copied, setCopied] = useState(false);
    const betUrl = `${APP_URL}/bets/${betId}`;
    
    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(betUrl);
            setCopied(true);
            toast.success('Link copied to clipboard!');
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            toast.error('Failed to copy link');
        }
    };
    
    return (
        <Button
            onClick={handleCopy}
            variant="outline"
            className={`border-white/10 text-white hover:bg-white/5 ${className}`}
            size="sm"
        >
            {copied ? (
                <>
                    <CheckCircle className="w-4 h-4 mr-2 text-[#00FF66]" />
                    Copied!
                </>
            ) : (
                <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Link
                </>
            )}
        </Button>
    );
}

// Full Social Share Panel
export function SocialSharePanel({ bet, winAmount, userRole, isWinner }) {
    if (!isWinner) return null;
    
    return (
        <div className="p-4 rounded-xl bg-gradient-to-r from-[#FFD700]/10 to-[#00FF66]/10 border border-[#FFD700]/30">
            <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#FFD700]/20 flex items-center justify-center">
                    <Share2 className="w-5 h-5 text-[#FFD700]" />
                </div>
                <div>
                    <h3 className="text-white font-semibold">Share Your Win</h3>
                    <p className="text-white/50 text-sm">Let everyone know about your victory</p>
                </div>
            </div>
            
            <div className="flex flex-wrap gap-3">
                <TwitterShareButton 
                    bet={bet} 
                    winAmount={winAmount} 
                    userRole={userRole}
                />
                <CopyLinkButton betId={bet.id} />
            </div>
            
            {/* Preview */}
            <div className="mt-4 p-3 rounded-lg bg-white/5 border border-white/5">
                <div className="text-xs text-white/40 mb-2">Preview:</div>
                <p className="text-sm text-white/70">
                    Just won <span className="text-[#00FF66] font-semibold">{winAmount.toFixed(4)} ETH</span> on Bet4Peer.
                </p>
                <p className="text-xs text-white/50 mt-1 truncate">
                    "{bet.description?.slice(0, 50)}..."
                </p>
            </div>
        </div>
    );
}

// Share Win Card (for Dashboard/History)
export function ShareWinCard({ bet, profit }) {
    const [showShare, setShowShare] = useState(false);
    
    if (profit <= 0) return null;
    
    return (
        <div className="relative">
            <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowShare(!showShare)}
                className="text-[#FFD700] hover:text-[#FFD700] hover:bg-[#FFD700]/10"
            >
                <Share2 className="w-4 h-4" />
            </Button>
            
            {showShare && (
                <div className="absolute right-0 top-full mt-2 z-50 p-4 rounded-xl bg-[#1a0a30] border border-white/10 shadow-2xl min-w-[280px]">
                    <div className="text-sm text-white font-semibold mb-3">
                        Share this win
                    </div>
                    <div className="flex gap-2">
                        <TwitterShareButton 
                            bet={bet} 
                            winAmount={profit}
                            userRole="winner"
                            className="flex-1"
                        />
                        <CopyLinkButton betId={bet.id} className="flex-1" />
                    </div>
                </div>
            )}
        </div>
    );
}

// Generate OG Image URL (placeholder - could be enhanced with actual image generation)
export function generateOGImageUrl(bet, winAmount) {
    // This could be enhanced to generate actual OG images using an API
    return `${APP_URL}/api/og/win?amount=${winAmount}&bet=${bet.id}`;
}
